<template>
    <div id="credit-card" class="content-spacer">
        <ViewHeader title="Credit Card Payment" />
        <div id="rest-of-body" class="drop-shadow-sides">
            <TotalDetail :isTipVisible="true" />
            <div v-show="ccFormLoaded" class="view-content">
                <form id="cc-form" ref="cc-form" novalidate>
                    <div class="field">
                        <label for="cc-number">Credit Card Number</label>
                        <div id="cc-number" class="braintree-form-control" />
                        <div v-if="!isNumberValid" class="error-message">
                            Invalid credit card number.
                        </div>
                    </div>
                    <div class="field">
                        <div class="inline">
                            <label for="cc-expiration">Expiration Date</label>
                            <div id="cc-expiration" class="braintree-form-control" />
                            <div v-if="!isExpirationValid" class="error-message">
                                Invalid date.
                            </div>
                        </div>
                        <div class="inline">
                            <div id="cc-cvv-label">
                                <div><label for="cc-cvv">{{ cvvLabel }}</label></div>&nbsp;
                                <div @click="showCvvInfo" class="info-mark">?</div>
                            </div>
                            <div id="cc-cvv" class="braintree-form-control" />
                            <div v-if="!isCvvValid" class="error-message">
                                Invalid CVV code.
                            </div>
                        </div>
                    </div>
                    <div class="field">
                        <label for="cc-name">Name on Card</label>
                        <input ref="cc-name" id="cc-name" class="input-secondary" type="text" required />
                        <div v-if="!isNameValid" class="error-message">
                            Name on card is required.
                        </div>
                    </div>
                    <button class="button-primary" type="submit">
                        Pay with <span id="card-brand">
                            {{ cardType }}
                        </span>
                    </button>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
    import ViewHeader from "../components/ViewHeader";
    import TotalDetail from "../components/TotalDetail";
    import { sweetAlert2 } from "../mixins/sweet-alert2";
    import { showBlocker } from "../mixins/show-blocker";
    import axios from "axios";
    import braintree from "braintree-web";
    import { mapState, mapGetters, mapMutations } from "vuex";
    import { paymentMethods } from "../constants/payment-methods";
    import { appSettings } from "../mixins/app-settings";

    export default {
        name: "CreditCardBraintree",
        components: {
            ViewHeader,
            TotalDetail
        },
        mixins: [sweetAlert2, showBlocker, appSettings],
        data() {
            return {
                cardType: "card",
                cvvLabel: "CVV",
                isNumberValid: true,
                isExpirationValid: true,
                isCvvValid: true,
                isNameValid: true,
                ccFormLoaded: false
            }
        },
        computed: {
            ...mapState({
                siteId: state => state.session.siteId,
                accountId: state => state.session.accountId,
                checkId: state => state.session.checkId,
                tip: state => state.session.tip,
                creditCardInitCompleted: state => state.session.creditCardInitCompleted,
                creditCardPaymentClient: state => state.session.creditCardPaymentClient,
                apiBaseUrl: state => state.session.apiBaseUrl
            }),
            ...mapGetters([
                "balanceWithTip"
            ])
        },
        methods: {
            ...mapMutations([
                "setLoadingStatus",
                "setPayment"
            ]),
            showCvvInfo() {
                let cvvInfoHtml = "<div id=\"cvv-info\">" +
                    "<div>The CVV number is a 3- or 4-digit security code, usually located on the back of your card.</div><br/>" +                                
                    "<img src=\"" + require("@/assets/images/back-of-credit-card.png") + "\" style=\"max-width:250px\" /></div>"

                this.swalAlertHtml(cvvInfoHtml, "About CVV");
            },
            setErrorMessageVisibility(id, isValid) {
                switch (id) {
                    case "cc-number": this.isNumberValid = isValid; break;
                    case "cc-expiration": this.isExpirationValid = isValid; break;
                    case "cc-cvv": this.isCvvValid = isValid; break;
                    case "cc-name": this.isNameValid = isValid; break;
                }
            },
            setValidityClasses(element, isValid) {
                if (isValid) {
                    element.classList.remove("input-invalid");
                    element.classList.add("input-valid");
                }
                else {
                    element.classList.remove("input-valid");
                    element.classList.add("input-invalid");
                }

                this.setErrorMessageVisibility(element.id, isValid);
            },
            validateInput(element) {
                // If empty, set to invalid.
                if (!element.value.trim()) {
                    this.setValidityClasses(element, false);
                    return false;
                }

                this.setValidityClasses(element, true);
                return true;
            },
            setupBraintreeCreditCardPayment() {
                // Make sure the client is fully initialized at this stage. If not, wait
                // a little bit for it to finish up.
                if (!this.creditCardInitCompleted) {
                    this.setLoadingStatus(true);
                    setTimeout(() => { this.setupBraintreeCreditCardPayment() }, 1000);
                    return;
                }
                
                // The attempt to initialize could have been done, but the initialization
                // might have failed. Ask user to try other payment methods if that's the case.
                if (this.creditCardPaymentClient === null) {
                    console.error("User requested credit card payment, but credit card payment unavailable.")
                    this.swalError("1006");
                    return;
                }
                                
                braintree.hostedFields.create(
                    {
                        client: this.creditCardPaymentClient,
                        styles: {
                            input: {
                                "font-size": "16px",
                                color: "#495057"
                            }
                        },
                        fields: {
                            number: {
                                selector: "#cc-number",
                                placeholder: "4111 1111 1111 1111"
                            },
                            cvv: {
                                selector: "#cc-cvv",
                                placeholder: "123"
                            },
                            expirationDate: {
                                selector: "#cc-expiration",
                                placeholder: "MM / YY"
                            }
                        }
                    },
                    (err, hostedFieldsInstance) => {
                        this.setupBraintreeValidation(err, hostedFieldsInstance);
                        this.setupBraintreeFormSubmit(hostedFieldsInstance);

                        // Turn off blocker in case it was turned on earlier.
                        this.setLoadingStatus(false);
                        this.ccFormLoaded = true;
                    });
            },
            setupBraintreeValidation(err, hostedFieldsInstance) {
                if (err) {
                    console.error(err);
                    this.swalError("1006");
                    return;
                }

                let ccName = this.$refs["cc-name"];

                ccName.addEventListener("input", () => {
                    this.validateInput(ccName);
                });

                hostedFieldsInstance.on("validityChange", (event) => {
                    let field = event.fields[event.emittedBy];

                    // Remove any previously applied error or warning classes.
                    field.container.classList.remove("input-valid");
                    field.container.classList.remove("input-invalid");

                    if (field.isValid) {
                        field.container.classList.add("input-valid");
                        this.setErrorMessageVisibility(field.container.id, true);
                    }
                    else if (field.isPotentiallyValid) {
                        // Skip adding classes if the field is
                        // not valid, but is potentially valid.

                        // Hide error message.
                        this.setErrorMessageVisibility(field.container.id, true);
                    }
                    else {
                        field.container.classList.add("input-invalid");
                        this.setErrorMessageVisibility(field.container.id, false);
                    }
                });

                hostedFieldsInstance.on("cardTypeChange", (event) => {
                    if (event.cards.length === 1) {
                        var card = event.cards[0];

                        // Change pay button to specify the type of card
                        // being used.
                        this.cardType = card.niceType;
                        this.cvvLabel = card.code.name;
                    }
                    else {
                        // reset to defaults
                        this.cardType = "card";
                        this.cvvLabel = "CVV";
                    }
                });
            },
            setupBraintreeFormSubmit(hostedFieldsInstance) {
                this.$refs["cc-form"].addEventListener("submit", (event) => {
                    event.preventDefault();

                    let formIsInvalid = false;
                    let state = hostedFieldsInstance.getState();
                    let ccName = this.$refs["cc-name"];

                    // Loop through the Hosted Fields and check
                    // for validity, apply the invalid class
                    // to the field container if invalid.
                    Object.keys(state.fields).forEach((field) => {
                        if (!state.fields[field].isValid) {
                            let fieldContainer = state.fields[field].container;
                            fieldContainer.classList.add("input-invalid");
                            this.setErrorMessageVisibility(fieldContainer.id, false);
                            formIsInvalid = true;
                        }
                    });

                    // Perform validations on the non Hosted Fields inputs.
                    if (!this.validateInput(ccName)) {
                        formIsInvalid = true;
                    }

                    if (formIsInvalid) {
                        // Skip tokenization request if any fields are invalid.
                        return;
                    }

                    this.setLoadingStatus(true);

                    hostedFieldsInstance.tokenize(
                        {
                            // Include the cardholderName in the tokenization request.
                            cardholderName: ccName.value
                        },
                        (err, payload) => {
                            if (err) {
                                // At this stage, customer has not been charged yet. Encourage
                                // them to give other payment methods a try.
                                this.setLoadingStatus(false);
                                console.error(err);
                                this.swalError("1006");
                                return;
                            }

                            this.sendPayloadToServer(payload);
                        });
                });
            },
            sendPayloadToServer(payload) {
                let oePayload =
                {
                    SiteId: this.siteId,
                    AccountId: this.accountId,
                    CheckId: this.checkId,
                    PaymentAmount: this.balanceWithTip,
                    TipAmount: this.tip,
                    PaymentNonce: payload.nonce
                }

                this.startProgressIndicator();

                axios.post(this.getApiBaseUrl() + "/payment/payment", oePayload)
                    .then(response => {
                        if (response.data) {
                            let payment = response.data;
                            payment.paymentName = paymentMethods.creditCard;
                            this.setPayment(payment);
                            this.$router.push({ name: "PaymentSummary" });
                        }
                        else {
                            this.$router.push({ name: "Error", params: { errorCode: "1010" } });
                        }
                    })
                    .catch(error => {
                        if (error.response &&
                            error.response.data &&
                            error.response.data.errorCode) {
                            switch (error.response.data.errorCode) {
                                case "1008":
                                case "1009":
                                    this.$router.push({ name: "Error", params: { errorCode: error.response.data.errorCode } });
                                    break;
                                default:
                                    if (error.response.data.errorMessage) {
                                        console.error(error.response.data.errorMessage);
                                    }
                                    this.$router.push({ name: "Error", params: { errorCode: "1010" } });
                            }
                        }
                        else {
                            console.error(error);
                            this.$router.push({ name: "Error", params: { errorCode: "1010" } });
                        }
                    })
                    .finally(() => {
                        this.stopProgressIndicator();
                    });
            }
        },
        mounted() {
            this.setupBraintreeCreditCardPayment();
        }
    }
</script>

<style scoped>
    #credit-card {
        display: flex;
        flex-direction: column;
    }

    #rest-of-body {
        flex-grow: 1;
        background-color: white;
        padding-bottom: 60px;
    }

    #cc-cvv-label div {
        display: inline-block;
    }

    .field {
        text-align: left;
        margin-bottom: 15px;
    }

    .braintree-form-control {
        margin-top: 5px;
    }

    input {
        width: 100%;
        height: 44px;
        font-size: 16px;
        box-sizing: border-box;
        color: #495057;
        margin-top: 5px;
    }

    div.inline {
        width: 49%;
    }

        div.inline:last-child {
            float: right;
        }

    .spacer {
        width: 10px;
    }

    button {
        width: 100%;
        margin-top: 15px;
    }

    label {
        margin-left: 5px;
    }
</style>